// Google Analytics 4 with GDPR Consent Mode v2, in "basic" mode (#1291).
//
// Privacy posture:
//   - Nothing from Google loads, and nothing is sent to it, until the visitor
//     accepts analytics cookies: gtag.js is only added to the page then. The
//     "advanced" mode this replaces loaded gtag.js for everyone and sent each
//     page view (address, IP) with consent denied, which French law (art. 82
//     loi Informatique et Libertés) very likely forbids without consent.
//   - Page views are sent only while analytics consent is on. Withdrawing it
//     after gtag.js has loaded sets Google's own opt-out flag
//     (window["ga-disable-<id>"]), so the loaded tag stops sending too.
//   - Analytics is disabled entirely in dev and when no measurement ID is
//     configured, so local/preview traffic never reaches GA.
//   - The user's choice lives in localStorage ("cookie-consent" as JSON
//     {analytics, marketing}) and is re-applied on every load.

import { redactSecretPaths } from "@/lib/secretPaths";

// Measurement ID comes from the build env (VITE_GA_ID) so prod and staging
// can differ; falls back to the production property.
const GA_ID = (import.meta.env.VITE_GA_ID as string | undefined) || "G-6TLZBSE756";
const GA_ENABLED = !import.meta.env.DEV && !!GA_ID;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// gtag.js is on the page (the visitor accepted analytics at least once).
let tagLoaded = false;
// The visitor's current analytics choice. Page views go out only while true.
let analyticsGranted = false;

// Canonical Google gtag shim: forward the live `arguments` object, NOT a
// rest-array. gtag.js processes the dataLayer command queue expecting the
// `arguments` shape; a plain array can be silently ignored, so the queued
// `config`/`event` commands never turn into hits and GA receives nothing.
// The `...args` signature is only there so the typed call sites below
// (gtag("consent", ...), gtag("event", ...)) compile — the body forwards
// `arguments` to stay byte-for-byte compatible with Google's official snippet.
function gtag(...args: unknown[]) {
  void args;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

// Prepare the command queue with every consent state denied. This only fills
// window.dataLayer: no script is loaded and nothing leaves the browser until
// updateConsent() grants analytics. No-op in dev / without a measurement ID.
export function initConsentMode() {
  if (!GA_ENABLED) return;
  // SSR guard — never touch window/document during server render.
  if (typeof window === "undefined" || typeof document === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;

  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

// Adds gtag.js to the page, once. The queue it then reads holds the denied
// defaults, the visitor's update and this config, in that order.
function loadTag() {
  if (tagLoaded || typeof document === "undefined") return;
  tagLoaded = true;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  gtag("config", GA_ID, {
    send_page_view: false,
    page_location: redactSecretPaths(window.location.href),
    page_referrer: redactSecretPaths(document.referrer),
  });
}

export function updateConsent(preferences: {
  analytics: boolean;
  marketing: boolean;
}) {
  if (!window.gtag) return;
  analyticsGranted = preferences.analytics;
  // Google's documented opt-out: a loaded tag sends nothing while it is true.
  (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] = !preferences.analytics;
  gtag("consent", "update", {
    analytics_storage: preferences.analytics ? "granted" : "denied",
    ad_storage: preferences.marketing ? "granted" : "denied",
    ad_user_data: preferences.marketing ? "granted" : "denied",
    ad_personalization: preferences.marketing ? "granted" : "denied",
  });
  if (preferences.analytics) loadTag();
}

// The previous page's cleaned address, reported as the next page's referrer:
// the browser's own document.referrer holds the raw one after a full page load.
let previousLocation: string | undefined;

// Reset and confirmation links carry a secret in their address (#1079), and
// Google must not receive it, consent or not. Nothing is sent before consent;
// after it, everything reported goes through redactSecretPaths, and the tag's
// current page is updated first so GA's own automatic events (scrolls, clicks)
// carry the cleaned address too.
export function trackPageView(path: string) {
  if (!window.gtag || !analyticsGranted) return;
  const pageLocation = redactSecretPaths(window.location.href);
  const pageReferrer = previousLocation ?? (typeof document === "undefined" ? undefined : redactSecretPaths(document.referrer));
  gtag("config", GA_ID, {
    send_page_view: false,
    page_location: pageLocation,
    page_referrer: pageReferrer,
    update: true,
  });
  gtag("event", "page_view", {
    page_path: redactSecretPaths(path),
    page_location: pageLocation,
    page_referrer: pageReferrer,
  });
  previousLocation = pageLocation;
}

export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
}

// Maps the stored "cookie-consent" value — current JSON shape OR the legacy
// string format ("all" / "custom" / "essential") — to {analytics, marketing}.
// Returns null when nothing is stored. Shared by restoreConsent() here and
// CookieConsent.loadPrefs() so the dialog and the live GA state never diverge.
// (The old banner saved "custom" whenever the user toggled ANYTHING on, so we
// treat both "all" and "custom" as full consent.)
export function parseStoredConsent(raw: string | null): ConsentPreferences | null {
  if (!raw) return null;
  try {
    const prefs = JSON.parse(raw);
    return { analytics: !!prefs.analytics, marketing: !!prefs.marketing };
  } catch {
    if (raw === "all" || raw === "custom") return { analytics: true, marketing: true };
    return { analytics: false, marketing: false }; // "essential" or anything unknown
  }
}

// Restore consent from localStorage on app load — re-applies the user's prior
// choice to GA, including legacy-format values.
export function restoreConsent() {
  const prefs = parseStoredConsent(typeof localStorage === "undefined" ? null : localStorage.getItem("cookie-consent"));
  if (prefs) updateConsent(prefs);
}
